import React, { useCallback, useMemo, useState } from 'react';
import { IconSearchSvg } from 'apps/sigma2-ui/src/components/svg/IconSearch';
import cn from 'classnames';
import { HeatmapTableSerializableProps } from 'libs/end-to-end/src/heatmap-analysis';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { TouchableTooltipCollectionHook } from 'apps/sigma2-ui/src/lib/hooks/useTouchableTooltipCollection';
import { TouchableTooltipCollectionItem } from 'apps/sigma2-ui/src/lib/hooks/useTouchableTooltipCollection';
import { SortIconSvg } from 'apps/sigma2-ui/src/components/svg/SortIcon';
import { FilterCheckableItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterCheckableItem';

/**
 * Interface for the HeatmapTable component props
 * @interface TableGridProps
 */
export type TableGridProps<TItem extends TouchableTooltipCollectionItem> =
  HeatmapTableSerializableProps & {
    tooltipProps?: TouchableTooltipCollectionHook<TItem>;
    tableClassName?: string;
    searchClassName?: string;
    displayBorder?: boolean;
    selectedCells?: number[];
    handleSelectCell?: (index: number, selected: boolean) => void;
  };

type SortDirection = 'asc' | 'desc' | 'none';

interface SortConfig {
  columnKey: string | null;
  direction: SortDirection;
}

/**
 * HeatmapTable component displays a matrix of metrics and their values across different groups
 * with color-coded cells based on impact scores.
 *
 * @component
 * @param {TableGridProps} props - Component props
 * @returns {React.ReactElement} Rendered HeatmapTable component
 */
export const TableGrid = <TItem extends TouchableTooltipCollectionItem>({
  backgroundColorClass,
  tableClassName,
  searchClassName,
  hideSearch,
  shouldScroll,
  header,
  body,
  tooltipProps,
  displayBorder,
  selectedCells,
  handleSelectCell,
}: TableGridProps<TItem>) => {
  const language = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    columnKey: null,
    direction: 'none',
  });

  const fallbackTooltipProps = useMemo(
    () => ({
      tooltipItem: null,
      renderTooltip: () => null,
      createTouchTarget: ({ children }: { children: React.ReactNode }) =>
        children,
    }),
    [],
  );

  const { tooltipItem, renderTooltip, createTouchTarget } =
    tooltipProps ?? fallbackTooltipProps;

  // Filter body rows based on search term
  const filteredBody = useMemo(() => {
    if (!searchTerm.trim()) {
      return body;
    }

    // Split search term into individual words
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);

    return body.filter((row) => {
      // Get the text content of the first column
      const firstColumnText = row.cells[0].content.toLowerCase();

      // Check if all search words are found in the row text
      return searchWords.every((word) => firstColumnText.includes(word));
    });
  }, [body, searchTerm]);

  // Handle column header click for sorting
  const handleSort = (columnKey: string) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.columnKey === columnKey) {
        // Cycling through: asc -> desc -> none
        switch (prevConfig.direction) {
          case 'asc':
            return { columnKey, direction: 'desc' };
          case 'desc':
            return { columnKey: null, direction: 'none' };
          case 'none':
            return { columnKey, direction: 'asc' };
          default:
            return { columnKey, direction: 'asc' };
        }
      } else {
        // New column selected, start with ascending
        return { columnKey, direction: 'asc' };
      }
    });
  };

  // Sort the body data based on current sort configuration
  const sortedBody = useMemo(() => {
    const filteredBodyWithOriginalIndex = filteredBody.map((row, index) => ({
      ...row,
      originalIndex: index,
    }));

    // Find the column index based on the header
    const columnIndex = sortConfig.columnKey
      ? header.findIndex(
          (headerCell) => headerCell.key === sortConfig.columnKey,
        )
      : 0;

    if (columnIndex === -1) {
      return filteredBodyWithOriginalIndex;
    }

    // Account for the header cell of each row. If no column is selected, sort by the header column (first column)
    const actualColumnIndex = sortConfig.columnKey
      ? columnIndex + 1
      : columnIndex;

    const sorted = [...filteredBodyWithOriginalIndex].sort((a, b) => {
      const aCell = a.cells[actualColumnIndex];
      const bCell = b.cells[actualColumnIndex];

      if (!aCell || !bCell) return 0;

      const aContent = aCell.content;
      const bContent = bCell.content;

      // Try to parse as numbers first (remove % and other non-numeric characters)
      const aNum = parseFloat(aContent.replace(/[^\d.-]/g, ''));
      const bNum = parseFloat(bContent.replace(/[^\d.-]/g, ''));

      let comparison = 0;

      if (!isNaN(aNum) && !isNaN(bNum)) {
        // Both are numbers
        comparison = aNum - bNum;
      } else {
        // Fall back to string comparison
        comparison = aContent.localeCompare(bContent);
      }

      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });

    return sorted;
  }, [filteredBody, sortConfig, header]);

  // Render sort indicator - always reserves space to prevent width changes
  const getSortIndicator = useCallback(
    (columnKey: string) => {
      const isActive = sortConfig.columnKey === columnKey;

      return (
        <span className="inline-block w-3 text-center">
          <SortIconSvg
            svg={{
              className: cn({
                'rotate-180': sortConfig.direction === 'asc',
                invisible: !isActive,
              }),
            }}
          />
        </span>
      );
    },
    [sortConfig.columnKey, sortConfig.direction],
  );

  const searchCell = useMemo(() => {
    return (
      <th
        key="search-cell"
        className={cn(
          'sticky',
          'left-0',
          'z-30',
          'text-left',
          'p-4',
          'font-medium',
          'text-sm',
          'md:w-[250px]',
          'min-h-16',
          backgroundColorClass,
          searchClassName,
        )}
      >
        <div
          className={cn(
            'relative',
            hideSearch ? 'invisible' : 'invisible md:visible',
          )}
        >
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            <IconSearchSvg />
          </span>
          <input
            type="search"
            placeholder={language.filterSearchBoxPlaceholder()}
            className="form-input max-w-[200px] h-10 rounded-md border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </th>
    );
  }, [backgroundColorClass, hideSearch, language, searchTerm, searchClassName]);

  const headRow = useMemo(() => {
    return [
      searchCell,
      ...header.map((cell) => (
        <th
          key={cell.key}
          className={cn(
            'p-1 font-medium text-xs text-center relative cursor-pointer hover:bg-gray-50 ',
            cell.className,
          )}
          onClick={() => handleSort(cell.key)}
        >
          <div className="justify-between flex items-center">
            <div className="flex items-center justify-center gap-1 md:px-3">
              <div className="flex justify-center relative h-full ">
                {createTouchTarget({
                  key: cell.key,
                  children: (
                    <span className="truncate hover:decoration-dotted hover:underline underline-offset-4">
                      {cell.label}
                    </span>
                  ),
                  fallback: <span className="truncate">{cell.label}</span>,
                })}
              </div>
            </div>
            {getSortIndicator(cell.key)}
          </div>
        </th>
      )),
    ];
  }, [searchCell, header, getSortIndicator, createTouchTarget]);

  return (
    <div>
      <div
        className={cn('relative', 'mb-4', hideSearch ? 'hidden' : 'md:hidden')}
      >
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          <IconSearchSvg />
        </span>
        <input
          type="search"
          placeholder={language.filterSearchBoxPlaceholder()}
          className="form-input w-full h-10 rounded-md border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div
        className={cn('relative w-full rounded-b-md', {
          'border-1 border-gray-200 border-solid shadow-2px shadow-black/5 py-4 px-4':
            displayBorder,
          'overflow-auto h-full': shouldScroll,
        })}
      >
        <table
          className={cn(
            'w-full border-collapse relative',
            backgroundColorClass,
            tableClassName,
          )}
        >
          <thead className="sticky top-0 ">
            <tr>{headRow}</tr>
          </thead>
          <tbody>
            {sortedBody.map((row) => (
              <tr key={row.key} className="align-middle group">
                {row.cells.map((cell, index) =>
                  index === 0 ? (
                    <td
                      key={`${row.key}-${cell.key}`}
                      className={cn(
                        'h-1 sticky left-0 z-10 p-1 text-xs font-medium w-[250px]',
                        backgroundColorClass,
                        cell.className,
                      )}
                    >
                      <div
                        className={cn(
                          'rounded-md p-1 md:p-3 w-full h-full group-hover:bg-royal-100',
                          backgroundColorClass,
                        )}
                      >
                        {selectedCells !== undefined ? (
                          <div className="-my-2">
                            <FilterCheckableItem
                              onChange={(newChecked) => {
                                handleSelectCell?.(
                                  row.originalIndex,
                                  newChecked,
                                );
                              }}
                              checked={selectedCells.includes(
                                row.originalIndex,
                              )}
                              translatedContent={cell.content}
                              itemValue={cell.content}
                            />
                          </div>
                        ) : (
                          cell.content
                        )}
                      </div>
                    </td>
                  ) : (
                    <td
                      key={`${row.key}-${cell.key}`}
                      className={cn(
                        'h-1 p-1 min-w-[60px] md:min-w-[120px] align-middle ',
                        cell.className,
                      )}
                    >
                      <div className="flex justify-center relative h-full ">
                        {tooltipProps &&
                          createTouchTarget({
                            key: cell.key,
                            children: (
                              <button
                                className={cn(
                                  'w-full h-full rounded-md p-1 md:p-3 text-center text-sm font-medium hover:opacity-80 transition-opacity duration-200 ',
                                  {
                                    'ring-1 ring-gray-500 ring-offset-2 shadow-lg':
                                      cell.key === tooltipItem?.key,
                                  },
                                )}
                                style={cell.style}
                              >
                                {cell.content}
                              </button>
                            ),
                          })}
                        {!tooltipProps && <div>{cell.content}</div>}
                      </div>
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {renderTooltip()}
      </div>
    </div>
  );
};
